'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import './TransactionRecordTable.css';
import ReactCountryFlag from 'react-country-flag';
import { getCodeList } from 'country-list';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

// Country list setup
const countryListObject = getCodeList();
const CountriesList = Object.entries(countryListObject).map(([code, name]) => ({
  code,
  name,
}));

// Country name mapping for common variations
const countryNameMapping = {
  iran: 'iran, islamic republic of',
  russia: 'russian federation',
  'south korea': 'korea, republic of',
  'north korea': "korea, democratic people's republic of",
  usa: 'united states',
  'united states': 'united states',
  uk: 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  vietnam: 'viet nam',
  bolivia: 'bolivia, plurinational state of',
  venezuela: 'venezuela, bolivarian republic of',
  tanzania: 'tanzania, united republic of',
  macedonia: 'macedonia, the former yugoslav republic of',
  moldova: 'moldova, republic of',
  syria: 'syrian arab republic',
  laos: "lao people's democratic republic",
  brunei: 'brunei darussalam',
  congo: 'congo, the democratic republic of the',
  'ivory coast': "côte d'ivoire",
  'czech republic': 'czechia',
  'east timor': 'timor-leste',
  swaziland: 'eswatini',
  myanmar: 'myanmar',
  burma: 'myanmar',
};

const countryCodeShortNames = {
  AE: 'UAE',
  GB: 'UK',
  US: 'USA',
};

const countryDisplayShortNames = {
  'united arab emirates': 'UAE',
  'united kingdom': 'UK',
  'united states': 'USA',
  'united states of america': 'USA',
};

const getCountryDisplayName = (countryName, countryCode) => {
  if (!countryName) return '---';

  if (countryCode && countryCodeShortNames[countryCode]) {
    return countryCodeShortNames[countryCode];
  }

  const shortName = countryDisplayShortNames[countryName.toLowerCase()];
  return shortName || countryName;
};

const TransactionRecordTable = ({ data = [] }) => {
  // Render country with flag
  const renderCountry = countryName => {
    if (!countryName) return '---';

    let countryMatch = CountriesList.find(
      country => country.name.toLowerCase() === countryName.toLowerCase()
    );

    // If no exact match, try with mapping
    if (!countryMatch) {
      const mappedName = countryNameMapping[countryName.toLowerCase()];
      if (mappedName) {
        countryMatch = CountriesList.find(
          country => country.name.toLowerCase() === mappedName.toLowerCase()
        );
      }
    }

    return countryMatch ? (
      <Box className="trx-record-country-content">
        <ReactCountryFlag countryCode={countryMatch.code} svg className="trx-record-flag" />
        <span>{getCountryDisplayName(countryName, countryMatch.code)}</span>
      </Box>
    ) : (
      <span>{getCountryDisplayName(countryName)}</span>
    );
  };

  // Use data directly since it's already paginated from server
  const currentRows = data;

  const formatDate = dateStr => {
    if (!dateStr) return '---';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getDateColor = dateStr => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    // Normalize both dates to remove the time portion
    const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffTime = inputDate.getTime() - todayDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // future = positive, past = negative

    // Convert days to weeks
    const diffWeeks = Math.abs(diffDays) / 7;

    let colorClass = '';
    if (diffWeeks < 2)
      colorClass = 'trx-record-date-green'; // Less than 2 weeks (past or future)
    else if (diffWeeks >= 2 && diffWeeks < 4)
      colorClass = 'trx-record-date-yellow'; // 2-4 weeks (past or future)
    else if (diffWeeks >= 4) colorClass = 'trx-record-date-red'; // 4 weeks or more (past or future)

    // Debug logging
    console.log(
      'Date:',
      dateStr,
      'Diff days:',
      diffDays,
      'Diff weeks:',
      diffWeeks,
      'Color class:',
      colorClass
    );

    return colorClass;
  };

  return (
    <>
      <Box className="trx-record-table-wrapper w-full">
        <TableContainer
          component={Paper}
          elevation={0}
          className="trx-record-table-container"
          sx={{ borderRadius: '8px' }}
        >
          <Table stickyHeader aria-label="transaction record table" className="trx-record-table">
            <TableHead className="trx-record-table-head">
              <TableRow>
                <TableCell className="trx-record-org-header">Organization Name</TableCell>
                <TableCell className="trx-record-txn-id-header">Transaction ID</TableCell>
                <TableCell className="trx-record-date-header">Transaction Date</TableCell>
                <TableCell align="right" className="trx-record-earning-header">
                  Total Earning (Riyals)
                </TableCell>
                <TableCell align="right" className="trx-record-deduction-header">
                  Bank Deduction (Riyals)
                </TableCell>
                <TableCell align="right" className="trx-record-amount-header">
                  Actual Amount (Riyals)
                </TableCell>
                <TableCell className="trx-record-country-header">Country</TableCell>
                <TableCell className="trx-record-owner-header">Owner Name</TableCell>
                <TableCell className="trx-record-contact-header">Owner Contact Number</TableCell>
                <TableCell className="trx-record-account-header">Account Holder Name</TableCell>
                <TableCell className="trx-record-address-header">Address</TableCell>
                <TableCell className="trx-record-bank-header">Bank Name</TableCell>
                <TableCell className="trx-record-iban-header">IBAN Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="trx-record-table-body">
              {currentRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" className="trx-record-no-data">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                currentRows.map((row, idx) => (
                  <TableRow key={idx} hover className="trx-record-table-row">
                    <TableCell className="trx-record-org-cell">{row.organizationName}</TableCell>
                    <TableCell className="trx-record-txn-id-cell">
                      {row.transactionId ?? '---'}
                    </TableCell>
                    <TableCell
                      className={`trx-record-date-cell ${getDateColor(row.transactionDate)}`}
                    >
                      {formatDate(row.transactionDate)}
                    </TableCell>
                    <TableCell align="right" className="trx-record-earning-cell">
                      {row.totalEarning === '' ? '---' : row.totalEarning?.toLocaleString?.()}
                    </TableCell>
                    <TableCell align="right" className="trx-record-deduction-cell">
                      {row.bankDeduction === '' ? '---' : row.bankDeduction?.toLocaleString?.()}
                    </TableCell>
                    <TableCell align="right" className="trx-record-amount-cell">
                      {row.actualAmount === '' ? '---' : row.actualAmount?.toLocaleString?.()}
                    </TableCell>
                    <TableCell className="trx-record-country-cell">
                      {row.country === '' ? '---' : renderCountry(row.country)}
                    </TableCell>
                    <TableCell className="trx-record-owner-cell">
                      {row.ownerName === '' ? '---' : row.ownerName}
                    </TableCell>
                    <TableCell className="trx-record-contact-cell">
                      {row.ownerContactNumber === '' ? '---' : row.ownerContactNumber}
                    </TableCell>
                    <TableCell className="trx-record-account-cell">
                      {row.accountHolderName === '' ? '---' : row.accountHolderName}
                    </TableCell>
                    <TableCell className="trx-record-address-cell">
                      {row.address === '' ? '---' : row.address}
                    </TableCell>
                    <TableCell className="trx-record-bank-cell">
                      {row.bankName === '' ? '---' : row.bankName}
                    </TableCell>
                    <TableCell className="trx-record-iban-cell">
                      {row.ibanNumber === '' ? '---' : row.ibanNumber}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

TransactionRecordTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      organizationName: PropTypes.string.isRequired,
      transactionId: PropTypes.string,
      transactionDate: PropTypes.string.isRequired,
      totalEarning: PropTypes.number.isRequired,
      bankDeduction: PropTypes.number.isRequired,
      actualAmount: PropTypes.number.isRequired,
      country: PropTypes.string,
      ownerName: PropTypes.string.isRequired,
      ownerContactNumber: PropTypes.string,
      accountHolderName: PropTypes.string,
      address: PropTypes.string,
      bankName: PropTypes.string,
      ibanNumber: PropTypes.string,
    })
  ),
};

export default TransactionRecordTable;
